/**
 * The form controls that are built on a Radix primitive.
 *
 * Separated from `Form.tsx` because `Form.tsx` is reachable from the sign-in
 * screen: it lands in the shell chunk, and everything it imports is
 * downloaded before a visitor has signed in. `@radix-ui/react-select` was the
 * largest single thing arriving that way, for a control that appears on
 * exactly one lazily-loaded route.
 *
 * # What the split is worth today: nothing, and that is the finding
 *
 * Moving these three out of `Form.tsx` changed the shell by 0.03 kB, because
 * `index.ts` still re-exports them and the shell imports `index.ts`. Measured
 * three ways, same build, `vite build`, gzip:
 *
 * | arrangement                                  | shell     | Settings |
 * |----------------------------------------------|-----------|----------|
 * | all in `Form.tsx`, barrel                     | 165.23 kB | 1.37 kB  |
 * | split to here, still re-exported by the barrel| 165.23 kB | 1.37 kB  |
 * | split, and Settings imports from here direct  | 160.09 kB | 7.97 kB  |
 *
 * So the rule, stated once because the next work package needs it: **a
 * component the barrel re-exports is in the shell chunk, whether or not the
 * shell uses it.** Rolldown drops the unused *binding* but still assigns the
 * module to the entry. Route-loading something means importing it by path and
 * accepting that `~/components` is no longer the single door.
 *
 * 5.1 kB of 165 kB did not justify that exception today. It will the moment
 * an editor lands: CodeMirror must be imported by path from the route that
 * uses it and must never appear in `index.ts`.
 *
 * These still read `Form.module.css`, still take their ids from `Field`'s
 * context, and are still exported from `~/components` beside the rest.
 */

import { Checkbox as RadixCheckbox, Select as RadixSelect, Switch as RadixSwitch } from 'radix-ui';
import { useId, type ReactNode } from 'react';

import { cx } from '~/lib/cx';
import { useField } from './Form';
import { Check, ChevronDown, ChevronUpDown } from './icons';

import styles from './Form.module.css';

/* ====================================================================== *
 * Checkbox and Switch
 *
 * Two components and not one with a `variant`, because they mean different
 * things: a checkbox is a value that will be saved when the form is submitted,
 * a switch takes effect immediately. Rendering one as the other tells the user
 * the wrong thing about when their change lands.
 * ====================================================================== */

export interface CheckboxProps {
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  /** Shown beside the box. Required: a checkbox with no label is unidentifiable. */
  children: ReactNode;
  className?: string;
}

export function Checkbox({ children, className, id, ...rest }: CheckboxProps) {
  const generated = useId();
  const controlId = id ?? generated;

  return (
    <div className={cx(styles.choice, className)}>
      <RadixCheckbox.Root id={controlId} className={styles.checkbox} {...rest}>
        <RadixCheckbox.Indicator className={styles.checkboxIndicator}>
          <Check size={12} />
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      <label htmlFor={controlId} className={styles.choiceLabel}>
        {children}
      </label>
    </div>
  );
}

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  children: ReactNode;
  className?: string;
}

export function Switch({ children, className, id, ...rest }: SwitchProps) {
  const generated = useId();
  const controlId = id ?? generated;

  return (
    <div className={cx(styles.choice, className)}>
      <RadixSwitch.Root id={controlId} className={styles.switch} {...rest}>
        <RadixSwitch.Thumb className={styles.switchThumb} />
      </RadixSwitch.Root>
      <label htmlFor={controlId} className={styles.choiceLabel}>
        {children}
      </label>
    </div>
  );
}

/* ====================================================================== *
 * Select
 * ====================================================================== */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** Required when the select is not inside a `Field`. */
  'aria-label'?: string;
  className?: string;
}

export function Select({
  options,
  placeholder,
  className,
  id,
  'aria-label': ariaLabel,
  ...rest
}: SelectProps) {
  const field = useField(id);

  return (
    <RadixSelect.Root {...rest}>
      <RadixSelect.Trigger
        // On the trigger, which is the focusable control. On the Root (where
        // `...rest` goes) it named nothing, and a select outside a Field had
        // no accessible name at all.
        aria-label={ariaLabel}
        id={id ?? field.controlId}
        className={cx(styles.selectTrigger, field.invalid && styles.controlInvalid, className)}
        aria-describedby={field.describedBy}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon className={styles.adornment}>
          <ChevronUpDown />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content className={styles.selectContent} position="popper" sideOffset={4}>
          <RadixSelect.ScrollUpButton className={cx(styles.selectScroll, styles.selectScrollUp)}>
            <ChevronDown />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className={styles.selectViewport}>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled ?? false}
                className={styles.selectItem}
              >
                <span className={styles.selectItemIndicator}>
                  <RadixSelect.ItemIndicator>
                    <Check size={12} />
                  </RadixSelect.ItemIndicator>
                </span>
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className={styles.selectScroll}>
            <ChevronDown />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
